/**
 * <s-product-gallery> — PDP gallery with thumbnails, swipe track and variant media sync.
 *
 * Markup contract:
 *   [data-gallery-track]                — horizontally scrolling, scroll-snapped track of slides.
 *   [data-gallery-slide][data-media-id] — one slide per product media item.
 *   [data-gallery-thumb][data-media-id] — thumbnail buttons; aria-current="true" marks the active one.
 *   [data-gallery-frame]                — the square main image frame; its height drives the
 *                                         --gallery-height custom property on [data-gallery-sync].
 *   data-initial-media                  — media id to show on load (the selected variant's image).
 *
 * Variant sync: the product-form partial renders [data-variant-media-id]. When
 * s-variant-picker swaps that partial, a MutationObserver reads the new id and
 * shows that slide, so no change to the variant/cart logic is needed.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #resizeObserver;
  #formObserver;
  #scrollTimer;
  #lastVariantMediaId = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.track = this.querySelector('[data-gallery-track]');
    this.frame = this.querySelector('[data-gallery-frame]');
    if (!this.track) return;

    this.addEventListener('click', this.#onThumbClick, opts);
    this.track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });

    this.#syncHeight();
    this.#watchVariant();

    const initial = this.dataset.initialMedia;
    if (initial) requestAnimationFrame(() => this.show(initial, false));
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    this.#formObserver?.disconnect();
    clearTimeout(this.#scrollTimer);
  }

  /** Scroll the track to the slide for mediaId and mark its thumbnail active. */
  show(mediaId, smooth = true) {
    const slide = this.track.querySelector(`[data-gallery-slide][data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth && !reduce ? 'smooth' : 'instant' });
    this.#setActive(mediaId);
  }

  #setActive(mediaId) {
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = thumb.dataset.mediaId === String(mediaId);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  #onThumbClick = (event) => {
    const thumb = event.target instanceof Element ? event.target.closest('[data-gallery-thumb]') : null;
    if (!thumb || !this.contains(thumb)) return;
    this.show(thumb.dataset.mediaId);
  };

  /** After a swipe settles, highlight the thumbnail of the slide in view. */
  #onScroll = () => {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(this.track.scrollLeft / width);
      const slide = this.track.querySelectorAll('[data-gallery-slide]')[index];
      if (slide) this.#setActive(slide.dataset.mediaId);
    }, 80);
  };

  /** Mirror the main image height onto the info column's max height (desktop only via CSS). */
  #syncHeight() {
    const target = this.closest('[data-gallery-sync]');
    if (!target || !this.frame || typeof ResizeObserver === 'undefined') return;
    this.#resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height) target.style.setProperty('--gallery-height', `${Math.round(height)}px`);
    });
    this.#resizeObserver.observe(this.frame);
  }

  #watchVariant() {
    const form = document.querySelector(`[data-product-form][data-product-id="${this.dataset.productId}"]`);
    if (!form) return;
    this.#lastVariantMediaId = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
    this.#formObserver = new MutationObserver(() => {
      const id = form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || '';
      if (id && id !== this.#lastVariantMediaId) {
        this.#lastVariantMediaId = id;
        this.show(id);
      }
    });
    this.#formObserver.observe(form, { childList: true, subtree: true });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
